import React from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// ─── Paleta (espelha as variáveis do projeto) ────────────────────────────────
const COLOR = {
  green:        "#35b18f", // círculo concluído / progresso / anel ativo
  greenDark:    "#1c9b6a", // rótulo de passo concluído
  greenShadow:  "rgba(53,177,143,0.25)", // halo do passo ativo
  circleBorder: "#d3d7dd", // borda de passos futuros
  labelFuture:  "#9aa1a9", // número e rótulo de passo futuro
  labelActive:  "#1a1f26", // rótulo do passo ativo (negrito)
  trackBg:      "#e5e7eb", // trilha cinza da barra de progresso
  // Professor (secondary — roxo/violeta suave)
  prof:         "#7c3aed",
  profShadow:   "rgba(124,58,237,0.22)",
  profDark:     "#5b21b6",
  // Resumo (sky azul)
  sky:          "#0284c7",
  skyShadow:    "rgba(2,132,199,0.22)",
  skyDark:      "#075985",
} as const;

// ─── Tipos ───────────────────────────────────────────────────────────────────
interface StepDef {
  label: string;
  variant?: "default" | "professor" | "resumo";
}

interface OrderStepperProps {
  /** Índice do passo atual (0-based) */
  activeStep: number;
  /** Total de passos */
  totalSteps: number;
  /** Conjunto de índices já concluídos */
  completedSteps: Set<number>;
  /** Definições de cada passo (label + variant opcional) */
  steps: StepDef[];
  /** Rótulo do ciclo exibido no cabeçalho (ex: "2026" ou "2027") */
  cycleYear?: string | number;
  /** Callbacks de navegação */
  goToPrevious: () => void;
  goToNext: () => void;
  goToStep: (index: number) => void;
}

// ─── Componente ──────────────────────────────────────────────────────────────
export const OrderStepper: React.FC<OrderStepperProps> = ({
  activeStep,
  totalSteps,
  completedSteps,
  steps,
  cycleYear,
  goToPrevious,
  goToNext,
  goToStep,
}) => {
  const activeLabel = steps[activeStep]?.label ?? "";

  // Largura da faixa verde: vai de 7% (centro do 1.º círculo) até 93%
  // (centro do último), proporcional ao passo atual.
  const progressPct =
    totalSteps > 1
      ? 7 + (activeStep / (totalSteps - 1)) * 86
      : 7;

  return (
    <Card className="shadow-lg overflow-hidden">
      <CardContent className="p-6">
        {/* Cabeçalho */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-xl font-bold" style={{ color: COLOR.labelActive }}>
            {activeLabel}
          </h2>
          <span className="text-sm" style={{ color: COLOR.labelFuture }}>
            Etapa {activeStep + 1} de {totalSteps}
            {cycleYear ? ` · Ciclo ${cycleYear}` : ""}
          </span>
        </div>

        {/* Barra de progresso + círculos */}
        <div className="relative mb-6">
          {/* Trilha cinza */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 14,
              left: "7%",
              right: "7%",
              height: 2,
              background: COLOR.trackBg,
              borderRadius: 1,
              zIndex: 0,
            }}
          />
          {/* Faixa verde */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 14,
              left: "7%",
              width: `${progressPct - 7}%`,
              height: 2,
              background: COLOR.green,
              borderRadius: 1,
              zIndex: 0,
              transition: "width 0.35s ease",
            }}
          />

          {/* Fileira de passos */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              position: "relative",
              zIndex: 1,
            }}
          >
            {steps.map((step, index) => {
              const isCompleted = completedSteps.has(index);
              const isActive    = index === activeStep;

              const variant = step.variant ?? "default";

              // Paleta ativa de acordo com variant
              const accentColor =
                variant === "professor" ? COLOR.prof
                : variant === "resumo"   ? COLOR.sky
                : COLOR.green;
              const accentShadow =
                variant === "professor" ? COLOR.profShadow
                : variant === "resumo"   ? COLOR.skyShadow
                : COLOR.greenShadow;
              const accentDark =
                variant === "professor" ? COLOR.profDark
                : variant === "resumo"   ? COLOR.skyDark
                : COLOR.greenDark;

              // Cores futuras: fundo sempre #fff (cobre a linha), suavização via rgba na borda/número
              const futureBorder = variant !== "default"
                ? `${accentColor}99`   // 60% opaco
                : COLOR.circleBorder;
              const futureText = variant !== "default"
                ? `${accentColor}99`
                : COLOR.labelFuture;

              const circleStyle: React.CSSProperties = {
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                margin: "0 auto",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "box-shadow 0.2s ease",
                ...(isCompleted
                  ? {
                      background: accentColor,
                      color: "#fff",
                      border: "none",
                    }
                  : isActive
                  ? {
                      background: "#fff",
                      color: accentColor,
                      border: `2px solid ${accentColor}`,
                      boxShadow: `0 0 0 4px ${accentShadow}`,
                    }
                  : {
                      background: "#fff",  // sempre sólido — cobre a linha atrás
                      color: futureText,
                      border: `2px solid ${futureBorder}`,
                    }),
              };

              const labelStyle: React.CSSProperties = {
                marginTop: 6,
                fontSize: 11,
                lineHeight: 1.3,
                textAlign: "center",
                whiteSpace: "normal",
                wordBreak: "break-word",
                color: isActive
                  ? COLOR.labelActive
                  : isCompleted
                  ? accentDark
                  : variant !== "default"
                  ? `${accentColor}99`  // futuro colorido, levemente suavizado
                  : COLOR.labelFuture,
                fontWeight: isActive ? 700 : 400,
              };

              return (
                <button
                  key={index}
                  onClick={() => goToStep(index)}
                  aria-label={`Ir para etapa ${index + 1}: ${step.label}`}
                  aria-current={isActive ? "step" : undefined}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: "none",
                    border: "none",
                    padding: "0 2px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div style={circleStyle}>
                    {isCompleted ? (
                      <Check size={13} strokeWidth={3} />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <span style={labelStyle}>{step.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navegação */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={goToPrevious}
            disabled={activeStep === 0}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>

          <Button
            onClick={goToNext}
            disabled={activeStep === totalSteps - 1}
            className="flex items-center gap-2"
          >
            Próximo
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderStepper;
